import { useState } from 'react';
import { useAuth } from './use-auth';

interface MFAVerificationProps {
    onCancel: () => void;
}

export function MFAVerification({ onCancel }: MFAVerificationProps) {
    const { verifyMFA, error } = useAuth();
    const [verificationCode, setVerificationCode] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);

    const handleVerify = (e: React.FormEvent) => {
        e.preventDefault();
        setVerifyError(null);
        setIsVerifying(true);

        void (async () => {
            try {
                await verifyMFA(verificationCode);
            } catch (err) {
                setVerifyError(err instanceof Error ? err.message : 'Verification failed');
            } finally {
                setIsVerifying(false);
            }
        })();
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="w-full max-w-md space-y-8 rounded-lg border bg-card p-8 shadow-lg">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight">Enter MFA Code</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Open your authenticator app to get your verification code
                    </p>
                </div>

                <form onSubmit={handleVerify} className="space-y-6">
                    {(error ?? verifyError) && (
                        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                            {error ?? verifyError}
                        </div>
                    )}

                    <div>
                        <label htmlFor="code" className="block text-sm font-medium">
                            Verification Code
                        </label>
                        <input
                            id="code"
                            name="code"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            required
                            value={verificationCode}
                            onChange={(e) => {
                                setVerificationCode(e.target.value.replace(/\D/g, ''));
                            }}
                            className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-center text-2xl tracking-widest ring-offset-background placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            placeholder="000000"
                            autoFocus
                        />
                        <p className="mt-2 text-xs text-muted-foreground">
                            Enter the 6-digit code from your authenticator app
                        </p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                            Back
                        </button>
                        <button
                            type="submit"
                            disabled={isVerifying || verificationCode.length !== 6}
                            className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isVerifying ? 'Verifying...' : 'Verify'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
